import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Formik } from 'formik'

import { breakpoints, colors } from '@/styles'
import Yup from '@/lib/yup'
import useMediaQuery from '@/hooks/use-media-query'
import { Button } from '@/components/button-new'
import { FieldInput } from '@/components/form'
import {
  InputClear,
  RoundCheckDarkGreenIcon,
  UnverifiedIcon
} from '@/components/icons'
import { Label } from '@/components/text'

const CouponTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-top: 16px;
  background: ${props => props.color || colors.green96};
  padding: 16px;
  padding-left: 14px;
  border-radius: 8px;
  position: relative;
`

const CouponTextSubWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-left: 8px;

  div:first-child {
    margin: 2px 0 8px;
  }
`

const StyledLabel = styled(Label)`
  line-height: 16px;
`

const CouponRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-top: 1em;
  ${props => props.displayMobile && 'flex-wrap: wrap'};
`

const InputWrapper = styled.div`
  flex: 1;
`

const StyledButton = styled(Button)`
  min-width: 124px;
  height: 60px;
`

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 12px;
  cursor: pointer;
  background: none;
  padding: 0;
`

const CouponSection = ({
  totalLoading,
  handleRemoveCouponCode,
  hasCouponData,
  couponData,
  invalidPromoCodeMessage,
  setInvalidPromoCodeMessage
}) => {
  const { t } = useTranslation(['translation', 'pricing'])
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const router = useRouter()
  const { query } = router
  const queryPromoCode = query.couponCode

  // Clear promo code field when promo code is removed from query
  const formikRef = useRef()
  useEffect(() => {
    if (formikRef.current && !queryPromoCode) {
      formikRef.current.setFieldValue('promo', '')
    }
  }, [queryPromoCode])

  const formOptions = () => ({
    initialValues: { promo: queryPromoCode },
    validationSchema: Yup.object().shape({
      promo: Yup.string()
    }),
    onSubmit: (values, { setSubmitting }) => {
      setInvalidPromoCodeMessage('')
      const promo = values.promo.trim().toUpperCase()
      if (!promo) return setSubmitting(false)
      const newQuery = { ...query, couponCode: promo }
      router.replace(
        {
          pathname: '/register',
          query: newQuery
        },
        undefined,
        { shallow: true }
      )
      setSubmitting(false)
    }
  })

  return (
    <>
      {invalidPromoCodeMessage && (
        <CouponTextWrapper color={colors.yellow96}>
          <ClearButton
            aria-label="Clear invalid coupon code alert"
            onClick={() => setInvalidPromoCodeMessage('')}
          >
            <InputClear width={17} height={17} />
          </ClearButton>
          <UnverifiedIcon width={20} height={20} />
          <CouponTextSubWrapper>
            <Label type="large" fontSize="14px">
              Invalid Coupon
            </Label>
            <StyledLabel type="medium" fontSize="12px">
              {invalidPromoCodeMessage}
            </StyledLabel>
          </CouponTextSubWrapper>
        </CouponTextWrapper>
      )}
      {hasCouponData && (
        <CouponTextWrapper>
          <RoundCheckDarkGreenIcon width={20} height={20} />
          <CouponTextSubWrapper>
            <Label type="large" fontSize="14px">
              {t('pricing:Coupon applied!')}
            </Label>
            <Label type="medium" fontSize="12px">
              {couponData.promoCode} - {couponData.coupon.name}
            </Label>
          </CouponTextSubWrapper>
        </CouponTextWrapper>
      )}
      <Formik innerRef={formikRef} {...formOptions()}>
        {({ isSubmitting, handleSubmit, setFieldValue }) => {
          const loading = isSubmitting || totalLoading
          return (
            <CouponRow displayMobile={displayMobile}>
              <InputWrapper>
                <FieldInput
                  id="promo"
                  name="promo"
                  disabled={hasCouponData}
                  placeholder={t(
                    'l.register.add_coupon_code.label',
                    'Add coupon code'
                  )}
                  onChange={e =>
                    setFieldValue('promo', e.target.value.toUpperCase())
                  }
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !loading) {
                      handleSubmit()
                    }
                  }}
                />
              </InputWrapper>
              {hasCouponData ? (
                <StyledButton
                  theme="deleteSecondary"
                  onClick={handleRemoveCouponCode}
                  id="landlord-register"
                  size="unset"
                  disabled={loading}
                  loading={isSubmitting}
                >
                  {t('pricing:Remove')}
                </StyledButton>
              ) : (
                <StyledButton
                  theme="primary"
                  onClick={handleSubmit}
                  id="landlord-register"
                  size="unset"
                  disabled={loading}
                  loading={loading}
                  type="submit"
                >
                  {t('pricing:Apply')}
                </StyledButton>
              )}
            </CouponRow>
          )
        }}
      </Formik>
    </>
  )
}

export default CouponSection
